import { Document, Page, Text, View, StyleSheet, Image, Font } from "@react-pdf/renderer";

const stripHtml = (html) => {
  if (!html) return "";
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<\/li>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("fr-FR", { year: "numeric", month: "short" });
};

const BRAND = "#05264E";
const ACCENT = "#3C65F5";
const LIGHT_BG = "#F2F6FD";
const GREY = "#888";
const TEXT = "#333";

const s = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    fontSize: 9,
    color: TEXT,
    backgroundColor: "#fff",
  },
  // ─── Header band ───────────────────────────────────────────────────────────
  header: {
    backgroundColor: BRAND,
    padding: "22 30 18 30",
    flexDirection: "row",
    alignItems: "center",
  },
  avatar: {
    width: 72,
    height: 72,
    borderRadius: 36,
    borderWidth: 3,
    borderColor: "#ffffff50",
    marginRight: 18,
    objectFit: "cover",
  },
  avatarPlaceholder: {
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: ACCENT,
    marginRight: 18,
    justifyContent: "center",
    alignItems: "center",
  },
  avatarInitials: {
    color: "#fff",
    fontSize: 22,
    fontFamily: "Helvetica-Bold",
  },
  headerRight: { flex: 1 },
  headerName: {
    fontFamily: "Helvetica-Bold",
    fontSize: 20,
    color: "#fff",
    marginBottom: 3,
  },
  headerRole: {
    fontSize: 11,
    color: "#A8C5FF",
    marginBottom: 6,
  },
  headerMeta: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
  },
  headerMetaItem: {
    fontSize: 8,
    color: "#C5D8FF",
    marginRight: 14,
    marginBottom: 2,
  },
  // ─── Body: sidebar + main ──────────────────────────────────────────────────
  body: {
    flexDirection: "row",
    flex: 1,
  },
  sidebar: {
    width: "33%",
    backgroundColor: LIGHT_BG,
    padding: "16 18",
    borderRightWidth: 1,
    borderRightColor: "#E0E9F8",
  },
  main: {
    width: "67%",
    padding: "16 22",
  },
  // ─── Section ───────────────────────────────────────────────────────────────
  sectionTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 8,
    color: ACCENT,
    textTransform: "uppercase",
    letterSpacing: 1.2,
    marginBottom: 7,
    marginTop: 14,
    paddingBottom: 3,
    borderBottomWidth: 1.5,
    borderBottomColor: ACCENT,
  },
  firstSection: {
    marginTop: 0,
  },
  // ─── Skills ────────────────────────────────────────────────────────────────
  skillsWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  skillTag: {
    backgroundColor: ACCENT,
    color: "#fff",
    fontSize: 7.5,
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 3,
    marginRight: 5,
    marginBottom: 5,
  },
  // ─── Language ──────────────────────────────────────────────────────────────
  langRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 7,
  },
  langName: { fontSize: 9, color: TEXT, fontFamily: "Helvetica-Bold" },
  langBadge: {
    fontSize: 7,
    color: ACCENT,
    backgroundColor: "#E0E9FF",
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
  },
  // ─── Sidebar contact ───────────────────────────────────────────────────────
  contactItem: {
    fontSize: 8.5,
    color: TEXT,
    marginBottom: 5,
    lineHeight: 1.4,
  },
  // ─── Experience / Education blocks ─────────────────────────────────────────
  block: {
    marginBottom: 11,
    paddingLeft: 10,
    borderLeftWidth: 2,
    borderLeftColor: ACCENT,
  },
  blockTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 10,
    color: BRAND,
    marginBottom: 2,
  },
  blockSub: {
    fontSize: 8.5,
    color: "#555",
    marginBottom: 1,
  },
  blockDate: {
    fontSize: 7.5,
    color: GREY,
    marginBottom: 3,
  },
  blockDesc: {
    fontSize: 8,
    color: TEXT,
    lineHeight: 1.5,
  },
  // ─── Certifications ────────────────────────────────────────────────────────
  certRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 7,
  },
  certName: {
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    color: BRAND,
    flex: 1,
  },
  certDate: {
    fontSize: 8,
    color: GREY,
    marginLeft: 8,
  },
  // ─── References ────────────────────────────────────────────────────────────
  refGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  refBlock: {
    width: "48%",
    marginBottom: 8,
    marginRight: "2%",
    padding: "6 8",
    backgroundColor: LIGHT_BG,
    borderRadius: 4,
    borderLeftWidth: 2,
    borderLeftColor: ACCENT,
  },
  refName: {
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    color: BRAND,
    marginBottom: 1,
  },
  refRole: { fontSize: 8, color: "#555", marginBottom: 2 },
  refContact: { fontSize: 7.5, color: GREY },
  // ─── Bio ───────────────────────────────────────────────────────────────────
  bio: {
    fontSize: 9,
    color: TEXT,
    lineHeight: 1.55,
    textAlign: "justify",
  },
  // ─── Footer ────────────────────────────────────────────────────────────────
  footer: {
    position: "absolute",
    bottom: 18,
    left: 30,
    right: 30,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 0.5,
    borderTopColor: "#ccc",
    paddingTop: 6,
  },
  footerText: { fontSize: 7, color: "#bbb" },
});

const levelLabel = (lvl) => {
  const m = { beginner: "Débutant", intermediate: "Intermédiaire", advanced: "Avancé" };
  return m[lvl] || lvl;
};

const Initials = ({ name }) => {
  const parts = (name || "?").split(" ");
  const initials = parts
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <View style={s.avatarPlaceholder}>
      <Text style={s.avatarInitials}>{initials}</Text>
    </View>
  );
};

const ResumePDF = ({ resume, user }) => {
  const experiences = resume?.experience || [];
  const educations = resume?.education || [];
  const certifications = resume?.certifications || [];
  const languages = resume?.languages || [];
  const references = resume?.reference || [];
  const skills = resume?.skills || [];

  const firstName = user?.first_name || "";
  const lastName = user?.last_name || "";
  const fullName = [firstName, lastName].filter(Boolean).join(" ") || user?.email || "Candidat";
  const userPhoto = user?.picture;
  const today = new Date().toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <Document title={`CV – ${fullName}`} author="Cible RH Emploi">
      <Page size="A4" style={s.page}>
        {/* ── Header ── */}
        <View style={s.header}>
          {userPhoto ? (
            <Image style={s.avatar} src={userPhoto} />
          ) : (
            <Initials name={fullName} />
          )}
          <View style={s.headerRight}>
            <Text style={s.headerName}>{fullName}</Text>
            {resume?.primary_role && (
              <Text style={s.headerRole}>{resume.primary_role}</Text>
            )}
            <View style={s.headerMeta}>
              {user?.email && <Text style={s.headerMetaItem}>✉  {user.email}</Text>}
              {user?.phone_number && <Text style={s.headerMetaItem}>✆  {user.phone_number}</Text>}
              {resume?.profession?.name && (
                <Text style={s.headerMetaItem}>⚑  {resume.profession.name}</Text>
              )}
              {resume?.years_experience && (
                <Text style={s.headerMetaItem}>
                  ◷  {resume.years_experience} an{resume.years_experience > 1 ? "s" : ""} d'exp.
                </Text>
              )}
              {resume?.salary_min && (
                <Text style={s.headerMetaItem}>
                  ⊕  Prétention : {Number(resume.salary_min).toLocaleString("fr-FR")} FCFA/mois
                </Text>
              )}
            </View>
          </View>
        </View>

        {/* ── Body ── */}
        <View style={s.body}>
          {/* ── Sidebar ── */}
          <View style={s.sidebar}>
            {/* Contact */}
            {(user?.email || user?.phone_number) && (
              <View>
                <Text style={[s.sectionTitle, s.firstSection]}>Contact</Text>
                {user?.email && <Text style={s.contactItem}>✉  {user.email}</Text>}
                {user?.phone_number && <Text style={s.contactItem}>✆  {user.phone_number}</Text>}
              </View>
            )}

            {/* Skills */}
            {skills.length > 0 && (
              <View>
                <Text style={s.sectionTitle}>Compétences</Text>
                <View style={s.skillsWrap}>
                  {skills.map((sk, i) => (
                    <Text key={i} style={s.skillTag}>{sk.name}</Text>
                  ))}
                </View>
              </View>
            )}

            {/* Languages */}
            {languages.length > 0 && (
              <View>
                <Text style={s.sectionTitle}>Langues</Text>
                {languages.map((lang, i) => (
                  <View key={i} style={s.langRow}>
                    <Text style={s.langName}>{lang.designation}</Text>
                    <Text style={s.langBadge}>{levelLabel(lang.level)}</Text>
                  </View>
                ))}
              </View>
            )}

            {/* Certifications */}
            {certifications.length > 0 && (
              <View>
                <Text style={s.sectionTitle}>Certifications</Text>
                {certifications.map((cert, i) => (
                  <View key={i} style={s.certRow}>
                    <Text style={s.certName}>{cert.designation}</Text>
                    {cert.date && (
                      <Text style={s.certDate}>{formatDate(cert.date)}</Text>
                    )}
                  </View>
                ))}
              </View>
            )}

            {/* References */}
            {references.length > 0 && (
              <View>
                <Text style={s.sectionTitle}>Références</Text>
                {references.map((ref, i) => (
                  <View key={i} style={s.refBlock}>
                    <Text style={s.refName}>{ref.name}</Text>
                    {ref.role && <Text style={s.refRole}>{ref.role}</Text>}
                    {ref.email && <Text style={s.refContact}>{ref.email}</Text>}
                    {ref.phone_number && (
                      <Text style={s.refContact}>{ref.phone_number}</Text>
                    )}
                  </View>
                ))}
              </View>
            )}
          </View>

          {/* ── Main ── */}
          <View style={s.main}>
            {/* About */}
            {resume?.bio && (
              <View>
                <Text style={[s.sectionTitle, s.firstSection]}>À propos de moi</Text>
                <Text style={s.bio}>{stripHtml(resume.bio)}</Text>
              </View>
            )}

            {/* Work Experience */}
            {experiences.length > 0 && (
              <View>
                <Text style={s.sectionTitle}>Expérience professionnelle</Text>
                {experiences.map((exp, i) => (
                  <View key={i} style={s.block}>
                    <Text style={s.blockTitle}>{exp.title}</Text>
                    <Text style={s.blockSub}>
                      {exp.company?.name || ""}
                      {exp.industry?.name ? "  ·  " + exp.industry.name : ""}
                    </Text>
                    <Text style={s.blockDate}>
                      {formatDate(exp.start_date)}
                      {" → "}
                      {exp.current_job ? "Présent" : formatDate(exp.end_date)}
                    </Text>
                    {exp.description && (
                      <Text style={s.blockDesc}>{stripHtml(exp.description)}</Text>
                    )}
                  </View>
                ))}
              </View>
            )}

            {/* Education */}
            {educations.length > 0 && (
              <View>
                <Text style={s.sectionTitle}>Formation</Text>
                {educations.map((edu, i) => (
                  <View key={i} style={s.block}>
                    <Text style={s.blockTitle}>
                      {edu.degree?.name || edu.degree || ""}
                      {edu.field_of_study ? "  –  " + edu.field_of_study : ""}
                    </Text>
                    <Text style={s.blockSub}>{edu.college?.name || ""}</Text>
                    <Text style={s.blockDate}>
                      {formatDate(edu.start_date)}
                      {" → "}
                      {edu.end_date ? formatDate(edu.end_date) : "En cours"}
                      {edu.gpa ? "  ·  Note : " + edu.gpa : ""}
                    </Text>
                  </View>
                ))}
              </View>
            )}
          </View>
        </View>

        {/* ── Footer ── */}
        <View style={s.footer} fixed>
          <Text style={s.footerText}>
            {fullName} — CV généré via Cible RH Emploi
          </Text>
          <Text style={s.footerText}>le {today}</Text>
        </View>
      </Page>
    </Document>
  );
};

export default ResumePDF;
